import * as React from 'react';
import { Label } from '@wayste/shadcn/components/label';
import { InfoTooltip } from '@wayste/shadcn/components/tooltip';
import { cn } from '@wayste/shadcn/lib/utils';

interface InputProps extends React.ComponentProps<'input'> {
    tooltip?: string;
    helperText?: string;
    label?: string;
    required?: boolean;
    startAdornment?: React.ReactNode;
}

/**
 * Input component for text input fields.
 *
 * @param tooltip - Optional tooltip text to display inside the input.
 * @param helperText - Optional helper text to display below the input. When the input is invalid, this text will be styled as error text.
 * @param label - Optional label text to display above the input.
 * @param startAdornment - Optional content pinned inside the left edge of the input, such as an icon or a unit. Sized for a few
 * characters; wider content needs a matching left padding override via `className`.
 */
function Input({ className, type, tooltip, helperText, label, startAdornment, ...props }: InputProps) {
    const isInvalid = props['aria-invalid'] === true || props['aria-invalid'] === 'true';
    const hasLabelOrHelperText = label || helperText;
    const hasStartAdornment = Boolean(startAdornment);
    const needsWrapper = tooltip || hasStartAdornment || hasLabelOrHelperText;

    const inputElement = (
        <input
            type={type}
            data-slot="input"
            className={cn(
                'file:text-text-primary placeholder:text-text-tertiary selection:bg-primary-default selection:text-primary-on-default-text',
                'shadow-xs bg-surface-tertiary flex h-10 w-full min-w-0 rounded-md border px-3 py-2.5 text-base outline-none transition-[color,box-shadow] md:text-sm',
                'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium',
                'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
                isInvalid
                    ? 'border-destructive-default hover:border-destructive-default'
                    : 'border-border-default hover:border-border-strong',
                isInvalid
                    ? 'focus:border-destructive-default focus-visible:border-destructive-default'
                    : 'focus:border-primary-border focus-visible:border-primary-border',
                isInvalid && 'ring-destructive-default/20 dark:ring-destructive-default/40',
                tooltip && 'pr-8',
                hasStartAdornment && 'pl-9',
                className,
            )}
            {...props}
        />
    );

    // If no wrapper needed, return input directly
    if (!needsWrapper) {
        return inputElement;
    }

    const inputField = (
        <div className="relative">
            {hasStartAdornment && (
                <div
                    data-slot="input-start-adornment"
                    className={cn(
                        'text-text-secondary pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm',
                        props.disabled && 'opacity-50',
                    )}
                >
                    {startAdornment}
                </div>
            )}
            {inputElement}
            {tooltip && (
                <div className="absolute inset-y-0 right-2 flex items-center">
                    <InfoTooltip
                        description={tooltip}
                        disabled={props.disabled}
                        className="size-5 p-0 hover:bg-transparent focus:ring-0 focus:ring-offset-0"
                    />
                </div>
            )}
        </div>
    );

    if (!hasLabelOrHelperText) {
        return inputField;
    }

    return (
        <div className="flex flex-col gap-2">
            {label && (
                <Label htmlFor={props.id} className="text-text-primary gap-x-0 text-xs font-medium leading-4 tracking-[0.24px]">
                    {label}
                    {props.required && <span className="text-destructive-default ml-1">*</span>}
                </Label>
            )}
            {inputField}
            {helperText && (
                <p
                    className={cn(
                        'm-0 text-xs leading-4',
                        isInvalid ? 'text-destructive-on-surface-text font-medium' : 'text-text-secondary font-normal',
                    )}
                >
                    {helperText}
                </p>
            )}
        </div>
    );
}

export { Input };
